import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {assertAndContinue} from 'Asserts';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  getConfigShapeAtConfigShapePath,
  setValueInConfig,
} from 'pipelines/generalized-step-config-editor/step-config-settings/StepConfigSettingsUtils';
import * as PipelineDAO from 'pipelines/network/PipelineDAO';
import {validatePipelineStepConfig} from 'pipelines/step-config-validation/validatePipelineStepConfig';
import {GetPipelineConfigSpecType, getStepTypeConfigShape} from 'pipelines/StepShapeUtils';
import {InlineBannerType} from 'ui-library/banners/InlineBanner';
import InlineBanner from 'ui-library/banners/InlineBanner';
import InlineBannerStack from 'ui-library/banners/InlineBannerStack';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import LoadingOverlay from 'ui-library/LoadingOverlay';
import {assertJSONObject, JSONValue} from 'util/Types';

import StepConfigEditorRouter from './editors/StepConfigEditorRouter';
import StepConfigEditorRouterV2 from './editors/StepConfigEditorRouterV2';
import makeInitialConfigState from './initial-values/makeInitialConfigState';
import {STEP_CONFIG_SETTINGS_V2} from './step-config-settings/StepConfigSettings';
import {StepConfigEditorContext} from './StepConfigEditorContext';
import {
  getInputDataFramesFromInfos,
  StepConfigUISchemaV2,
  StepConfigUISchema,
  ConfigDataFrameInfo,
  HAS_BACKEND_FIELD_GENERATOR,
} from './StepConfigEditorTypes';
import {
  ConfigEditorStepType,
  stepTypeIsConfigEditorSupported,
  isUiSchemaForStepTypeV2,
} from './stepTypeIsConfigEditorSupported';

const FAILED_STEP_WARNING_BANNER_READ_ONLY_TEXT =
  'The configuration for this transformation is no longer valid.';

const stepShapeOverrideGlobalState = makeUseGlobalClientState<GetPipelineConfigSpecType<'shape'> | null>(
  null
);

/** @package */
export const FAILED_STEP_WARNING_BANNER_TEXT = `${FAILED_STEP_WARNING_BANNER_READ_ONLY_TEXT} Please reconfigure it.`;

interface StepConfigEditorPaneProps {
  stepType: ConfigEditorStepType;
  stepVersion: number;
  config: Record<string, JSONValue> | null;
  setConfig: (newConfig: Record<string, JSONValue>) => void;
  dataFramesInfos: Record<string, Array<ConfigDataFrameInfo>>;
  clientStateId: string;
  error?: string;
  stepIsFailed?: boolean;
  readOnly?: boolean;
  forceSingleColumnDisplay?: boolean;
  stepId?: string;
}

export default function StepConfigEditorPane({
  stepType,
  stepVersion,
  dataFramesInfos,
  config,
  setConfig,
  clientStateId,
  error,
  stepIsFailed,
  readOnly,
  forceSingleColumnDisplay,
  stepId,
}: StepConfigEditorPaneProps): JSX.Element {
  // Memoize so that the context provider doesn't re-render the entire tree every time this component
  // rerenders e.g. due to a state change (also because we'd get an eslint error otherwise)
  const contextValue = useMemo(() => ({clientStateId, dataFramesInfos}), [dataFramesInfos, clientStateId]);
  // Step shape override - only used when backend field generation is used to override default step shape.
  const [stepShapeOverride, setStepShapeOverride] =
    stepShapeOverrideGlobalState.useGlobalClientState(clientStateId);

  // TODO(BNCH-148338): Remove V1 path once StepConfigEditorRouter is fully deprecated.
  const uiSchema = isUiSchemaForStepTypeV2(stepType)
    ? STEP_CONFIG_SETTINGS_V2[stepType][stepVersion]?.uiSchema
    : undefined;
  const hasBackendFieldGeneration =
    (uiSchema as Record<symbol, boolean> | undefined)?.[HAS_BACKEND_FIELD_GENERATOR] ?? false;
  // Only used if step has backend field generation.
  const [isLoadingBackendFields, setIsLoadingBackendFields] = useState<boolean>(false);

  const requestCounterRef = useRef(0);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const generateBackendFields = useCallback(
    (newConfig: Record<string, JSONValue>) => {
      setIsLoadingBackendFields(true);
      const requestId = ++requestCounterRef.current;
      async function generateConfig(): Promise<void> {
        try {
          const response = await PipelineDAO.generateStepConfig(
            getStepTypeConfigShape(stepType, stepVersion),
            newConfig,
            stepType,
            stepId
          );
          if (requestId !== requestCounterRef.current) {
            return;
          }
          setStepShapeOverride(response.stepShape);
          setConfig(response.config);
        } finally {
          if (requestId === requestCounterRef.current) {
            setIsLoadingBackendFields(false);
          }
        }
      }
      generateConfig();
    },
    [stepType, stepId, stepVersion, setStepShapeOverride, setConfig]
  );

  const BACKEND_FIELD_GENERATION_DEBOUNCE_MS = 500;
  const debouncedGenerateBackendFields = useCallback(
    (newConfig: Record<string, JSONValue>) => {
      if (debounceTimerRef.current != null) {
        clearTimeout(debounceTimerRef.current);
      }
      // Invalidate any in-flight request so it doesn't overwrite state during the debounce window
      requestCounterRef.current++;
      debounceTimerRef.current = setTimeout(() => {
        generateBackendFields(newConfig);
      }, BACKEND_FIELD_GENERATION_DEBOUNCE_MS);
    },
    [generateBackendFields]
  );
  useEffect(
    () => () => {
      if (debounceTimerRef.current != null) {
        clearTimeout(debounceTimerRef.current);
      }
    },
    []
  );

  useEffect(() => {
    setStepShapeOverride(null);
    setIsLoadingBackendFields(false);
    if (debounceTimerRef.current != null) {
      clearTimeout(debounceTimerRef.current);
    }
    requestCounterRef.current++;
    if (hasBackendFieldGeneration) {
      generateBackendFields(config ?? makeInitialConfigState(stepType, stepVersion));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- config, generateBackendFields, and other deps intentionally excluded to avoid infinite re-render loops
  }, [stepType, stepId, stepVersion]);

  if (!stepTypeIsConfigEditorSupported(stepType, stepVersion) || stepType === PipelineStepType.CHART_VIEW) {
    assertAndContinue(false, `StepConfigEditorPane used for ${stepType} v${stepVersion}`);
    return <InlineBanner bannerType={InlineBannerType.WARNING} text="No configuration is available." />;
  }

  const errors = [
    ...(error ? [error] : []),
    ...(config
      ? validatePipelineStepConfig(
          stepType,
          stepVersion,
          config,
          getInputDataFramesFromInfos(dataFramesInfos)
        ).errors
      : []),
  ];

  return (
    <LoadingOverlay isLoading={isLoadingBackendFields} isFaded={true}>
      <StepConfigEditorContext.Provider value={contextValue}>
        {isUiSchemaForStepTypeV2(stepType) ? (
          <StepConfigEditorRouterV2
            configShape={stepShapeOverride ?? getStepTypeConfigShape(stepType, stepVersion)}
            config={config ?? makeInitialConfigState(stepType, stepVersion)}
            dataTestAttrs={{}}
            onChangeConfig={(
              oldConfig: Record<string, JSONValue | null>,
              path: Array<string>,
              newValue: JSONValue
            ) => {
              const newConfig = setValueInConfig(oldConfig ?? {}, path, newValue);
              setConfig(newConfig);
              if (hasBackendFieldGeneration) {
                const currentConfigShape = stepShapeOverride ?? getStepTypeConfigShape(stepType, stepVersion);
                const fieldSpec = getConfigShapeAtConfigShapePath(path, currentConfigShape, newConfig);
                if (fieldSpec.kind === 'string') {
                  debouncedGenerateBackendFields(newConfig);
                } else {
                  generateBackendFields(newConfig);
                }
              }
            }}
            uiSchema={uiSchema as StepConfigUISchemaV2}
            disabled={readOnly}
          />
        ) : (
          <StepConfigEditorRouter
            configShape={stepShapeOverride ?? getStepTypeConfigShape(stepType, stepVersion)}
            config={config ?? makeInitialConfigState(stepType, stepVersion)}
            dataTestAttrs={{}}
            onChangeConfig={(config) => {
              // This should be true as long as we have a list of named variables as the step config shape.
              assertJSONObject(config);
              if (hasBackendFieldGeneration) {
                generateBackendFields(config);
              } else {
                setConfig(config);
              }
            }}
            uiSchema={uiSchema as StepConfigUISchema}
            isTopLevel={true}
            disabled={readOnly}
            forceSingleColumnDisplay={forceSingleColumnDisplay}
          />
        )}
        <InlineBannerStack
          messages={errors.map((error) => ({bannerType: InlineBannerType.WARNING, text: error}))}
        />
        {stepIsFailed && (
          <InlineBanner
            bannerType={InlineBannerType.WARNING}
            text={readOnly ? FAILED_STEP_WARNING_BANNER_READ_ONLY_TEXT : FAILED_STEP_WARNING_BANNER_TEXT}
          />
        )}
      </StepConfigEditorContext.Provider>
    </LoadingOverlay>
  );
}
